import { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';

type Props = {
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
  /** Label for the entity, e.g. "shop(s)", "praticien(s)", "client(s)" */
  entityLabel: string;
  count: number;
  isLoading?: boolean;
};

/**
 * Two-step confirmation modal for definitive (hard) delete.
 * Step 1: Warn that this is a definitive, irreversible delete and ask to continue.
 * Step 2: Final confirmation with explicit message that this is a definitive delete.
 */
export function ConfirmDefinitiveDeleteModal({
  isOpen,
  onConfirm,
  onClose,
  entityLabel,
  count,
  isLoading = false,
}: Props) {
  const [step, setStep] = useState<1 | 2>(1);

  useEffect(() => {
    if (isOpen) setStep(1);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (isLoading) return;
    setStep(1);
    onClose();
  };

  if (step === 1) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
          <div className="flex items-center space-x-2 mb-4">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
            <h3 className="text-lg font-semibold text-red-700">Suppression définitive</h3>
          </div>
          <p className="text-gray-700 mb-6">
            <strong>Attention :</strong> vous demandez une <strong>suppression définitive</strong> de{' '}
            <strong>{count}</strong> {entityLabel}. Cette action est <strong>irréversible</strong> et toutes les données
            liées seront supprimées. Aucune récupération ne sera possible.
          </p>
          <p className="text-gray-600 text-sm mb-6">
            Souhaitez-vous continuer vers la confirmation ?
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={() => setStep(2)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Continuer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 ${isLoading ? 'cursor-wait' : ''}`}>
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div className="flex items-center space-x-2 mb-4">
          <AlertCircle className="w-6 h-6 text-red-700 flex-shrink-0" />
          <h3 className="text-lg font-semibold text-red-700">Dernière confirmation</h3>
        </div>
        <p className="text-gray-700 mb-6">
          <strong>Suppression définitive :</strong> confirmez-vous la suppression définitive et irréversible de{' '}
          <strong>{count}</strong> {entityLabel} ? Cette action ne peut pas être annulée.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50"
          >
            {isLoading ? 'Suppression...' : 'Supprimer définitivement'}
          </button>
        </div>
      </div>
    </div>
  );
}
